// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import { FacetModel } from "@insite/client-framework/Types/ApiModels";
import { AccordionSectionPresentationProps, ManagedAccordionSection } from "@insite/mobius/AccordionSection";
import Checkbox, {
    CheckboxPresentationProps,
    StyleProps as CheckboxStyleProps,
} from "@insite/mobius/Checkbox/Checkbox";
import CheckboxGroup, { CheckboxGroupProps } from "@insite/mobius/CheckboxGroup/CheckboxGroup";
import Link, { LinkPresentationProps } from "@insite/mobius/Link/Link";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import FancyScroll, { FancyScrollRefProps } from "@sc/App/Components/Common/FancyScroll";
import React, { FC, useState } from "react";
import { css } from "styled-components";

export interface ProductListFilterAccordionSectionStyles {
    accordionSection?: AccordionSectionPresentationProps;
    checkBoxGroup?: CheckboxGroupProps;
    checkBox?: CheckboxPresentationProps;
    checkBoxSelected?: CheckboxPresentationProps;
    seeMoreLink?: LinkPresentationProps;
    seeLessLink?: LinkPresentationProps;
    // XNG-Changes
    facetCount?: TypographyPresentationProps;
    facetFilterTextField?: TextFieldPresentationProps;
    scrollableContainer?: InjectableCss;
    facetFilterTextFieldWrapper?: InjectableCss;
}

export const productListFilterAccordionSectionStyles: ProductListFilterAccordionSectionStyles = {
    accordionSection: {
        panelProps: {
            css: css`
                overflow-y: auto;
                overflow-x: hidden;
                max-height: 350px;
            `,
        },
    },
    checkBoxGroup: {
        css: css`
            width: 100%;
            &:not(:last-child) {
                padding-bottom: 5px;
            }
        `,
    },
    checkBox: {
        css: css<CheckboxStyleProps>`
            align-items: center;
        `,
        typographyProps: {
            css: css`
                word-break: break-all;
            `,
        },
    },
    checkBoxSelected: {
        css: css<CheckboxStyleProps>`
            align-items: center;
        `,
        typographyProps: {
            css: css`
                word-break: break-all;
            `,
        },
    },
    seeMoreLink: {
        css: css`
            padding-top: 5px;
        `,
    },
    seeLessLink: {
        css: css`
            padding-top: 5px;
        `,
    },
};

export interface Props {
    title: string;
    facets: FacetModel[];
    onChangeFacet: (facet: FacetModel) => void;
    showMoreLimit: number;
    extendedStyles?: ProductListFilterAccordionSectionStyles;
    expandByDefault: boolean;
    // XNG-changes
    filterable?: boolean;
}

const ProductListFiltersAccordionSection: FC<Props> = ({
    title,
    facets,
    onChangeFacet,
    showMoreLimit,
    expandByDefault,
    extendedStyles,
    // XNG-changes
    filterable,
}) => {
    const [styles] = React.useState(() => mergeToNew(productListFilterAccordionSectionStyles, extendedStyles));
    const [expanded, setExpanded] = useState(false);
    // <XNG-Changes>
    const [toggled, setToggled] = useState(false);
    const [facetFilter, setFacetFilter] = useState("");
    const parentBoxRef = React.createRef<HTMLDivElement>();
    const fancyScrollRef = React.createRef<FancyScrollRefProps>();
    let limitedFacets: FacetModel[] = [];
    if (filterable) {
        if (facetFilter) {
            limitedFacets = facets.filter(f => f.name.toLowerCase().indexOf(facetFilter.toLowerCase()) >= 0);
        } else {
            limitedFacets = facets;
        }
    } else if (expanded) {
        limitedFacets = facets;
    } else {
        limitedFacets = facets?.slice(0, showMoreLimit);
    }

    // </XNG-Changes>

    const anySelected = facets.find(f => f.selected) !== undefined;

    React.useEffect(() => {
        fancyScrollRef.current?.updateScrollable();
    }, [expanded, toggled]);

    return (
        <ManagedAccordionSection
            title={title}
            {...styles.accordionSection}
            initialExpanded={expandByDefault || anySelected}
            onTogglePanel={open => setToggled(open)}
        >
            {filterable && (
                <StyledWrapper {...styles.facetFilterTextFieldWrapper}>
                    <TextField
                        value={facetFilter}
                        placeholder={translate("Search By Keyword")}
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) => setFacetFilter(event.target.value)}
                        {...styles.facetFilterTextField}
                    />
                </StyledWrapper>
            )}
            <FancyScroll refElement={parentBoxRef} ref={fancyScrollRef} shadowHeight="8px">
                <StyledWrapper
                    className="scrollable-container"
                    ref={parentBoxRef}
                    {...{ filterable, scrollable: limitedFacets.length > showMoreLimit, limitNumber: showMoreLimit }}
                    {...styles.scrollableContainer}
                >
                    {limitedFacets?.map(facet => (
                        <CheckboxGroup key={facet.id} {...styles.checkBoxGroup}>
                            <Checkbox
                                {...styles.checkBox}
                                checked={facet.selected}
                                onChange={() => onChangeFacet(facet)}
                                data-test-selector={`facetFilter${facet.id}`}
                            >
                                {facet.name}
                                {facet.count !== -1 && (
                                    <Typography {...styles.facetCount}>{`(${facet.count})`}</Typography>
                                )}
                            </Checkbox>
                        </CheckboxGroup>
                    ))}
                    {/* XNG-Changes: add filterable to conditions */}
                    {!filterable &&
                        facets &&
                        facets.length > showMoreLimit &&
                        (expanded ? (
                            <Link aria-label={translate("Show Less")} {...styles.seeLessLink} onClick={() => setExpanded(false)}>
                                {translate("Show Less")}
                            </Link>
                        ) : (
                            <Link aria-label={translate("Show More")} {...styles.seeMoreLink} onClick={() => setExpanded(true)}>
                                {translate("Show More")}
                            </Link>
                        ))}
                </StyledWrapper>
            </FancyScroll>
        </ManagedAccordionSection>
    );
};

export default ProductListFiltersAccordionSection;
